import React, { useState } from "react";
import SectionHeading from "./SectionHeading";
import {
  Server,
  ShieldCheck,
  TrendingUp,
  Database,
  Layers3,
  Wrench,
  Code2,
} from "lucide-react";

const apiFeatures = [
  {
    icon: <Server size={20} />,
    title: "Robust Architecture",
    description:
      "Our APIs are built on a strong architectural foundation, leveraging industry standards to ensure high reliability, scalability, and maintainability.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Secure Transactions",
    description:
      "We implement OAuth2, JWT, and encrypted payload mechanisms to protect your sensitive endpoints and guarantee secure data exchange.",
  },
  {
    icon: <TrendingUp size={20} />,
    title: "High Scalability",
    description:
      "Designed from the ground up to handle high concurrency, spike traffic, and massive throughput without performance bottlenecks.",
  },
];

const technologies = [
  {
    number: "1",
    tag: "Python",
    title: "Python & Django / FastAPI",
    description:
      "Ideal for rapid development, AI integrations, data pipelines, and highly computational backend services.",
    color: "bg-blue-50 text-blue-600 border-blue-200",
    badge: "bg-blue-100 text-blue-700",
  },
  {
    number: "2",
    tag: "Node.js",
    title: "Node.js & Express / NestJS",
    description:
      "Event-driven, non-blocking I/O model for asynchronous, real-time microservices and lightweight APIs.",
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
  },
  {
    number: "3",
    tag: "Ruby",
    title: "Ruby on Rails",
    description:
      "Clean syntax and powerful convention-over-configuration paradigms for fast product delivery and maintainable web services.",
    color: "bg-rose-50 text-rose-600 border-rose-200",
    badge: "bg-rose-100 text-rose-700",
  },
  {
    number: "4",
    tag: "PHP & Java",
    title: "PHP (Laravel) & Java Spring",
    description:
      "Enterprise-grade frameworks providing robust ecosystem tools, flexibility, and bulletproof server stability.",
    color: "bg-purple-50 text-purple-600 border-purple-200",
    badge: "bg-purple-100 text-purple-700",
  },
];

const dbFeatures = [
  {
    icon: <Database size={22} />,
    title: "SQL Relational Databases",
    description:
      "Expert schema design, indexing, and optimization for PostgreSQL, MySQL, and Oracle to ensure data integrity and query performance.",
  },
  {
    icon: <Layers3 size={22} />,
    title: "NoSQL & Caching",
    description:
      "High-throughput document and key-value stores including MongoDB, Cassandra, and Redis for ultra-fast, dynamic schema requirements.",
  },
  {
    icon: <ShieldCheck size={22} />,
    title: "Automated Backup & Recovery",
    description:
      "Continuous automated replication, point-in-time recovery, and disaster prevention to keep your critical data 100% resilient.",
  },
  {
    icon: <Wrench size={22} />,
    title: "Monitoring & Maintenance",
    description:
      "Proactive query profiling, index optimization, and continuous monitoring to guarantee seamless database operation.",
  },
];

export default function WebServiceCoreTech() {
  const [activeTab, setActiveTab] = useState("api");

  const tabs = [
    { id: "api", label: "Custom API Development", icon: Code2 },
    { id: "tech", label: "Server-Side Scripting", icon: Server },
    { id: "db", label: "Database Management", icon: Database },
  ];

  return (
    <section className="bg-[#F8F8FB] py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mx-auto">
          <SectionHeading
            eyebrow="Core Capabilities"
            title="Backend Engineering & Core Tech"
            description="From custom API architectures and multi-language server-side code to high-performance SQL and NoSQL database management — we engineer robust backend foundations."
            theme="light"
            titleClassName="text-[#111827]"
            descriptionClassName="text-[16px] leading-8"
          />
        </div>

        {/* Tab Switcher Navigation */}
        <div className="flex flex-wrap justify-center items-center gap-3 mt-10 mb-12">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${isActive
                  ? "bg-[#355dff] text-white shadow-lg shadow-[#355dff]/30 scale-105"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-[#355dff]/40 hover:text-[#355dff]"
                  }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab 1: API Development */}
        {activeTab === "api" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-500 animate-fadeIn">
            {apiFeatures.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#355dff] mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500 leading-7 text-[15px]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Tab 2: Server-Side Scripting */}
        {activeTab === "tech" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-500 animate-fadeIn">
            {technologies.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-7 transition duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base border ${item.color}`}>
                  {item.number}
                </div>
                <span className={`inline-block mt-4 px-3 py-1 rounded-full text-[11px] font-semibold ${item.badge}`}>
                  {item.tag}
                </span>
                <h3 className="mt-4 text-[18px] font-bold text-gray-900 leading-6">
                  {item.title}
                </h3>
                <p className="mt-3 text-gray-500 text-[14px] leading-6">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Tab 3: Database Management */}
        {activeTab === "db" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-500 animate-fadeIn">
            {dbFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-[#ECECEC] rounded-2xl p-8 hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#355dff] mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-[15px] leading-7">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
